import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  acknowledgeGuestEvent,
  askIdRescan,
  CheckinCard as Card,
  CheckinCardGuest,
  CheckinIdCheck,
  copyCheckinLink,
  IdCheckCode,
  markIdChecked,
  undoIdChecked,
  markCheckinLinkCopied,
  reissueCheckinLink,
  revokeCheckinLink,
  sendCheckinLink,
  signedRulesPdfUrl,
  verifyGuestInPerson,
} from '../../../../api/stays-checkin-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill, StatusTone } from '../../../status-pill/status-pill';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { formatDateTime, formatTime } from '../../../../function-utils/locale';
import { stayDates } from '../../../../utils/stays-stage';
import toast from '../../../../utils/toast';
import { palette, radius, shadow, spacing, typography } from '../../../../style-utils/theme';
import { up } from '../../../../style-utils/media-queries';
import { StayGuideRow } from '../guide/stay-guide-row';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';

/**
 * ONE STAY'S CHECK-IN, FOR THE HOST (plan S6, D-host-guest-cards; strategy doc
 * "Incoming guest cards": guest name, verification status, signed rules,
 * payment collected, one-click resend).
 *
 * Times are instants printed on the VIEWER'S device clock (R-T12: owners and
 * managers read their own clock; the Check-in tab carries the building's clock
 * note). Dates are the building's calendar days, printed as they are.
 *
 * Every write needs stays:manage (`canManage`); without it the card is a
 * read-out, and "View PDF" needs only stays:read.
 */

const Wrap = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Rows = styled.dl`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.xs};
  margin: 0;

  ${up('md')} {
    grid-template-columns: minmax(${spacing.huge}, max-content) 1fr;
    column-gap: ${spacing.md};
  }
`;

const Dt = styled.dt`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

const Dd = styled.dd`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const People = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Person = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

const Muted = styled.span`
  color: ${palette.slate};
`;

const LINK_WORDS: Record<string, string> = {
  none: 'Not made yet',
  live: 'Not sent',
  completed: 'Completed',
  revoked: 'Revoked',
  expired: 'Expired',
};

const band = (g: CheckinCardGuest) => (g.band === 'adult' ? 'adult' : 'child');

/** IS0: why an in-house ID needs a look, in plain words (the check codes never reach the guest). */
export const ID_CHECK_WORDS: Record<IdCheckCode, string> = {
  'A-1': 'The code strip on the document could not be read',
  'A-2': 'The details differ from the code strip',
  'A-3': 'The document has expired by arrival',
  'A-4': 'The age does not match the booking',
  'A-5': 'The name does not match the booking',
  'A-6': 'The same document as another guest',
};

/** "Typed by the guest · The document has expired by arrival" — staff only (R-IS0.10). */
export const idReasonText = (c: CheckinIdCheck): string =>
  [c.method === 'manual' ? 'Typed by the guest' : '', ...c.failed_checks.map(code => ID_CHECK_WORDS[code] || code)]
    .filter(Boolean)
    .join(' · ');

const ID_TONE: Record<CheckinIdCheck['review_state'], StatusTone> = {
  auto_confirmed: 'success',
  confirmed: 'success',
  needs_review: 'warning',
  rejected: 'alert',
};

// `desk`: does the organisation have Lobbero Desk (Independent Stays IS1)? A
// Stays-only host has no desk, so an in-person check is just that.
export const personPill = (g: CheckinCardGuest, desk = true): { tone: StatusTone; text: string } => {
  // IS0 (R-IS0.6): the server's own words for an in-house check; never "verified".
  if (g.id_check) {
    const c = g.id_check;
    const by = c.review_state === 'confirmed' && c.checked_by_name ? ` by ${c.checked_by_name}` : '';

    return { tone: ID_TONE[c.review_state], text: `${c.label}${by}` };
  }
  if (g.mode === 'declared') return g.status === 'declared' ? { tone: 'neutral', text: 'Details added' } : { tone: 'warning', text: 'Details needed' };
  if (g.mode === 'not_required') return { tone: 'neutral', text: 'No ID needed' };
  if (g.mode === 'in_person') return { tone: 'success', text: desk ? 'Checked at the desk' : 'Checked in person' };
  if (g.status === 'verified' || g.status === 'manual') return { tone: 'success', text: 'ID verified' };
  if (g.status === 'pending') return { tone: 'warning', text: 'In progress' };
  if (g.status === 'failed') return { tone: 'alert', text: "Couldn't verify" };

  return { tone: 'neutral', text: 'Not started' };
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "3 guests (2 adults, 1 child)". */
export const partyText = (c: Card): string => {
  const total = (c.party.adults ?? 0) + (c.party.minors ?? 0);
  if (c.party.adults == null) return plural(c.party.booked, 'guest', 'guests');

  return `${plural(total, 'guest', 'guests')} (${plural(c.party.adults, 'adult', 'adults')}, ${plural(
    c.party.minors ?? 0,
    'child',
    'children'
  )})`;
};

/** "2 of 2 adults verified · 1 child added" (IS0 in-house: "2 of 2 adults' IDs done · 1 needs a look"). */
export const identityText = (c: Card): string => {
  const i = c.identity;
  const inhouse = i.guests.some(g => !!g.id_check);
  const parts = inhouse
    ? [`${i.verified} of ${i.required} ${i.required === 1 ? "adult's ID" : "adults' IDs"} done`]
    : [`${i.verified} of ${i.required} ${i.required === 1 ? 'adult' : 'adults'} verified`];
  if (inhouse && i.needs_look) parts.push(`${i.needs_look} ${i.needs_look === 1 ? 'needs' : 'need'} a look`);
  if (i.children) parts.push(`${i.declared} of ${plural(i.children, 'child', 'children')} added`);

  return parts.join(' · ');
};

export const linkText = (c: Card): string => {
  if (c.link.status === 'live' && c.link.sent_at) {
    const via = c.link.sent_via === 'email' ? 'by email' : c.link.sent_via === 'whatsapp' ? 'on WhatsApp' : 'as a copied link';

    return `Sent ${via} ${formatDateTime(c.link.sent_at)}`;
  }

  return LINK_WORDS[c.link.status] || c.link.status;
};

export const CheckinCardView: React.FC<{
  card: Card;
  canManage: boolean;
  onChanged: (next: Card | null) => void;
}> = ({ card, canManage, onChanged }) => {
  const desk = useDeskInPlan();
  const [busy, setBusy] = useState<string | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [inPerson, setInPerson] = useState<CheckinCardGuest | null>(null);
  const [rescanFor, setRescanFor] = useState<CheckinCardGuest | null>(null);
  const [note, setNote] = useState('');
  const first = card.lead_first_name || 'the guest';
  const open = !card.access_revoked && ['live', 'completed', 'none'].includes(card.link.status);

  const run = async (key: string, fn: () => Promise<Card | null | void>, ok?: string) => {
    if (busy) return;
    setBusy(key);
    try {
      const next = await fn();
      if (next !== undefined) onChanged(next as Card | null);
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  const send = (resend: boolean) =>
    run(resend ? 'resend' : 'send', async () => {
      const out = await sendCheckinLink(card.reservation_id, resend);
      if (out.delivery.rotated) toast('A new link was made — the old one no longer works.');

      return out.card;
    }, resend ? 'Check-in link sent again' : 'Check-in link sent');

  const copy = (channel: 'copy' | 'whatsapp') =>
    run(channel, async () => {
      const out = await copyCheckinLink(card.reservation_id);
      if (out.rotated) toast('A new link was made — the old one no longer works.');
      if (channel === 'copy') await navigator.clipboard.writeText(out.message_text);
      else window.open(out.whatsapp_url, '_blank', 'noopener,noreferrer');
      await markCheckinLinkCopied(card.reservation_id, channel);

      return undefined;
    }, channel === 'copy' ? 'Message copied' : undefined);

  const viewPdf = () =>
    run('pdf', async () => {
      if (!card.rules.signature_id) return undefined;
      const { url } = await signedRulesPdfUrl(card.rules.signature_id);
      window.open(url, '_blank', 'noopener,noreferrer');

      return undefined;
    });

  const resendReason = card.link.resend_available_at
    ? `Sent ${plural(card.link.emails_last_hour, 'time', 'times')} recently. Try again at ${formatTime(card.link.resend_available_at)}.`
    : !card.has_email
      ? 'This stay has no guest email.'
      : null;
  const completed = !!card.completed_at;

  return (
    <Wrap aria-label={`Check-in for ${card.lead_name}`} data-reservation={card.reservation_id}>
      <Head>
        <Title>{card.lead_name}</Title>
        <StatusPill tone={completed ? 'success' : card.trip_confirmed_at ? 'warning' : 'neutral'} dot>
          {completed ? 'Check-in complete' : card.trip_confirmed_at ? 'In progress' : 'Not started'}
        </StatusPill>
      </Head>
      <Rows>
        <Dt>Stay</Dt>
        <Dd>{stayDates(card.check_in, card.check_out)}</Dd>
        <Dt>Party</Dt>
        <Dd>
          <span>{partyText(card)}</span>
          {card.party_mismatch ? (
            <span>
              <StatusPill tone="warning">{`Booked for ${plural(card.party.booked, 'guest', 'guests')}`}</StatusPill>
            </span>
          ) : null}
        </Dd>
        <Dt>ID check</Dt>
        <Dd>
          <span>{identityText(card)}</span>
          <People>
            {card.identity.guests.map(g => {
              const pill = personPill(g, desk);
              const canInPerson = canManage && !completed && (g.mode === 'vendor')
                && !['verified', 'manual'].includes(g.status);
              const idc = g.id_check || null;
              const reason = idc && (idc.review_state === 'needs_review' || idc.review_state === 'rejected') ? idReasonText(idc) : '';

              return (
                <Person key={g.guest_id}>
                  <span>{`${g.first_name} ${g.last_name}`.trim()}</span>
                  <Muted>{band(g)}</Muted>
                  <StatusPill tone={pill.tone}>{pill.text}</StatusPill>
                  {reason ? <Muted>{reason}</Muted> : null}
                  {canManage && idc && idc.can_confirm ? (
                    <AppButton
                      variant="ghost"
                      size="compact"
                      onClick={() => void run(`check-${g.guest_id}`, () => markIdChecked(g.guest_id), 'ID marked as checked')}
                      loading={busy === `check-${g.guest_id}`}
                      loadingLabel="Saving"
                      aria-label={`Mark ${g.first_name}'s ID as checked`}
                    >
                      Mark as checked
                    </AppButton>
                  ) : null}
                  {canManage && idc && idc.can_undo ? (
                    <AppButton
                      variant="ghost"
                      size="compact"
                      onClick={() => void run(`undo-${g.guest_id}`, () => undoIdChecked(g.guest_id), 'Check undone')}
                      loading={busy === `undo-${g.guest_id}`}
                      loadingLabel="Saving"
                      aria-label={`Undo the check of ${g.first_name}'s ID`}
                    >
                      Undo check
                    </AppButton>
                  ) : null}
                  {canManage && idc && idc.can_ask_rescan ? (
                    <AppButton
                      variant="ghost"
                      size="compact"
                      onClick={() => setRescanFor(g)}
                      aria-label={`Ask ${g.first_name} to scan their ID again`}
                    >
                      Ask to scan again
                    </AppButton>
                  ) : null}
                  {canInPerson ? (
                    <AppButton
                      variant="ghost"
                      size="compact"
                      onClick={() => setInPerson(g)}
                      aria-label={`Verified in person: ${g.first_name}`}
                    >
                      Verified in person
                    </AppButton>
                  ) : null}
                </Person>
              );
            })}
          </People>
        </Dd>
        <Dt>House rules</Dt>
        <Dd>
          {card.rules.signed_at ? (
            <>
              <span>{`Signed ${formatDateTime(card.rules.signed_at)} · ${card.rules.kind === 'drawn' ? 'Drawn' : 'Typed'} · Version ${card.rules.version}`}</span>
              {card.rules.mail_failed ? <Muted>Email not delivered — download and send it yourself.</Muted> : null}
              <span>
                <AppButton
                  variant="ghost"
                  size="compact"
                  onClick={() => void viewPdf()}
                  loading={busy === 'pdf'}
                  loadingLabel="Opening"
                  aria-label={`View signed house rules for ${card.lead_name}`}
                >
                  View PDF
                </AppButton>
              </span>
            </>
          ) : card.rules.required ? (
            <span>Not signed</span>
          ) : (
            <Muted>No house rules set</Muted>
          )}
        </Dd>
        <Dt>Payment</Dt>
        <Dd>{card.payment.label}</Dd>
        <Dt>Link</Dt>
        <Dd>
          <span>{linkText(card)}</span>
          {card.link.opened_count ? <Muted>{`Opened ${plural(card.link.opened_count, 'time', 'times')}`}</Muted> : null}
        </Dd>
        {canManage ? (
          <>
            {/* Plan S7.6: the stay's guide PIN, and when its door codes were first shown. */}
            <Dt>Guide</Dt>
            <Dd>
              <StayGuideRow reservationId={card.reservation_id} canManage={canManage} />
            </Dd>
          </>
        ) : null}
        {card.events.length ? (
          <>
            <Dt>From the guest</Dt>
            <Dd>
              <People>
                {card.events.map(e => (
                  <Person key={e.id}>
                    <span>
                      {e.kind === 'arrived'
                        ? `Says they've arrived ${formatDateTime(e.at)}`
                        : e.kind === 'checked_out'
                          ? `Says they've checked out ${formatDateTime(e.at)}`
                          : `Asked for help ${formatDateTime(e.at)}${e.note ? `: “${e.note}”` : ''}`}
                    </span>
                    {e.kind === 'help' && !e.acknowledged && canManage ? (
                      <AppButton
                        variant="ghost"
                        size="compact"
                        onClick={() => void run(`ack-${e.id}`, async () => {
                          await acknowledgeGuestEvent(e.id);

                          return { ...card, events: card.events.map(x => (x.id === e.id ? { ...x, acknowledged: true } : x)) };
                        })}
                        aria-label={`Mark the help request of ${formatDateTime(e.at)} as seen`}
                      >
                        Mark as seen
                      </AppButton>
                    ) : null}
                  </Person>
                ))}
              </People>
            </Dd>
          </>
        ) : null}
      </Rows>

      {canManage && open ? (
        <Actions>
          {!card.link.sent_at || card.link.status === 'none' ? (
            <ActionWithReason reason={card.has_email ? null : 'This stay has no guest email.'}>
              {describedBy => (
                <AppButton
                  variant="primary"
                  size="compact"
                  disabled={!card.has_email}
                  aria-describedby={describedBy}
                  loading={busy === 'send'}
                  loadingLabel="Sending"
                  onClick={() => void send(false)}
                  aria-label={`Send the check-in link to ${first} by email`}
                >
                  Send by email
                </AppButton>
              )}
            </ActionWithReason>
          ) : (
            <ActionWithReason reason={resendReason}>
              {describedBy => (
                <AppButton
                  variant="secondary"
                  size="compact"
                  disabled={!!resendReason}
                  aria-describedby={describedBy}
                  loading={busy === 'resend'}
                  loadingLabel="Sending"
                  onClick={() => void send(true)}
                  aria-label={`${completed ? 'Send again' : 'Resend'} the check-in link to ${first}`}
                >
                  {completed ? 'Send again' : 'Resend'}
                </AppButton>
              )}
            </ActionWithReason>
          )}
          <AppButton variant="secondary" size="compact" loading={busy === 'copy'} loadingLabel="Copying"
            onClick={() => void copy('copy')} aria-label={`Copy the check-in message for ${first}`}>
            Copy link
          </AppButton>
          <AppButton variant="secondary" size="compact" loading={busy === 'whatsapp'} loadingLabel="Opening"
            onClick={() => void copy('whatsapp')} aria-label={`Send the check-in link to ${first} on WhatsApp`}>
            WhatsApp
          </AppButton>
          {card.link.status === 'live' || card.link.status === 'completed' ? (
            <AppButton variant="ghost" size="compact" onClick={() => setRevokeOpen(true)}
              aria-label={`Revoke ${first}'s check-in link`}>
              Revoke link
            </AppButton>
          ) : (
            <AppButton variant="ghost" size="compact" loading={busy === 'reissue'} loadingLabel="Making a new link"
              onClick={() => void run('reissue', async () => (await reissueCheckinLink(card.reservation_id)).card, 'New check-in link made')}
              aria-label={`Make a new check-in link for ${first}`}>
              Make a new link
            </AppButton>
          )}
        </Actions>
      ) : null}

      <ConfirmDialog
        open={revokeOpen}
        title={`Revoke ${first}'s check-in link`}
        message="The link stops working at once, and so do the personal links of the people with them. You can make a new link later."
        primaryLabel={`Revoke ${first}'s link`}
        primaryVariant="destructive"
        dismissLabel="Keep link"
        onPrimary={async () => {
          await run('revoke', () => revokeCheckinLink(card.reservation_id), 'Check-in link revoked');
          setRevokeOpen(false);
        }}
        onDismiss={() => setRevokeOpen(false)}
      />
      <ConfirmDialog
        open={!!rescanFor}
        title="Ask for a new photo of the ID"
        message={
          rescanFor
            ? `${rescanFor.first_name}'s details are not accepted. Their check-in page asks them to scan the ID again. Use "Send again" to remind them.`
            : ''
        }
        primaryLabel={rescanFor ? `Ask ${rescanFor.first_name} to scan again` : 'Ask to scan again'}
        dismissLabel="Cancel"
        onPrimary={async () => {
          const g = rescanFor;
          if (g) await run(`rescan-${g.guest_id}`, () => askIdRescan(g.guest_id), 'New photo asked for');
          setRescanFor(null);
        }}
        onDismiss={() => setRescanFor(null)}
      />
      <ConfirmDialog
        open={!!inPerson}
        title="Confirm the ID in person"
        message={inPerson ? `Confirm you checked ${inPerson.first_name}'s ID document in person.` : ''}
        primaryLabel={inPerson ? `Confirm: ${inPerson.first_name}'s ID checked` : 'Confirm'}
        dismissLabel="Cancel"
        onPrimary={async () => {
          const g = inPerson;
          if (g) await run('in-person', () => verifyGuestInPerson(g.guest_id, note.trim() || undefined), 'ID check recorded');
          setInPerson(null);
          setNote('');
        }}
        onDismiss={() => {
          setInPerson(null);
          setNote('');
        }}
      >
        <CountedTextarea id="in-person-note" label="Note (optional)" value={note} max={280} rows={2}
          onChange={e => setNote(e.target.value.slice(0, 280))} />
      </ConfirmDialog>
    </Wrap>
  );
};
